import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Dropdown } from 'react-bootstrap';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Link, useNavigate } from 'react-router-dom';
import './Auth.css';
import logoIcon from '../../assets/images/logo-icon.png';
import webappImg from '../../assets/images/webapp_img.svg';
import gextLogo from '../../assets/images/gext_logo.svg';
import { useTranslation } from 'react-i18next';

function Register() {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Carica la lingua preferita dall'utente se disponibile
    const preferredLanguage = localStorage.getItem('preferredLanguage');
    if (preferredLanguage) {
      i18n.changeLanguage(preferredLanguage);
    }
  }, [i18n]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem('preferredLanguage', language);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return setError(t('auth.register.errors.passwordMismatch'));
    }

    try {
      setError('');
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      
      // Invia email di verifica
      await sendEmailVerification(userCredential.user);
      
      // Effettua il logout immediato dopo la registrazione
      await auth.signOut();
      
      // Reindirizza alla pagina di login con il messaggio di verifica
      navigate('/login?verification=pending');
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError(t('auth.register.errors.emailInUse'));
      } else {
        setError(t('auth.register.errors.generic', { message: error.message }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="auth-container">
      <div className="auth-left-panel">
        <div className="gext-logo">
          <img src={gextLogo} alt="GEXT Logo" />
        </div>
        <div className="auth-illustration">
          <img src={webappImg} alt="Gestione riparazioni" />
        </div>
        <div className="auth-left-content">
          <h1>{t('auth.register.title').split(' ')[0]}<br />{t('auth.register.title').split(' ').slice(1).join(' ')}</h1>
          <p>{t('auth.register.subtitle')}</p>
        </div>
      </div>

      <div className="auth-right-panel">
        <div className="brand-header-top">
          <div className="brand-logo">
            <img src={logoIcon} alt="PhoneDesk Logo" className="logo-icon" />
            <span>PhoneDesk</span>
          </div>
          <div className="language-selector">
            <Dropdown align="end">
              <Dropdown.Toggle variant="link" id="language-dropdown" className="language-toggle">
                <i className="bi bi-globe"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleLanguageChange('it')}>Italiano</Dropdown.Item>
                <Dropdown.Item onClick={() => handleLanguageChange('en')}>English</Dropdown.Item>
                <Dropdown.Item onClick={() => handleLanguageChange('zh')}>中文</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <div className="auth-form-container">
          <div className="auth-logo">
            <h2>{t('auth.register.title')}</h2>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('auth.register.form.email')}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.register.form.emailPlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('auth.register.form.password')}</Form.Label>
              <div className="password-input-group">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <Button 
                  variant="link" 
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t('auth.register.form.confirmPassword')}</Form.Label>
              <div className="password-input-group">
                <Form.Control
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <Button 
                  variant="link" 
                  className="password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                >
                  <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </Button>
              </div>
            </Form.Group>

            <Button
              className="w-100 auth-submit-btn"
              type="submit"
              disabled={loading}
            >
              {t('auth.register.form.submit')}
            </Button>
          </Form>

          <div className="auth-footer">
            {t('auth.register.form.haveAccount')} <Link to="/login">{t('auth.register.form.login')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register; 